import { useState } from 'react';
import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    verticalListSortingStrategy,
    useSortable,
    arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { iconMap } from './iconMap';
import {ReactComponent as Loading} from '../../../images/icons/General/loading-01.svg';

const GripIcon = () => (
    <svg viewBox="0 0 16 16" fill="currentColor" width="14" height="14" aria-hidden="true">
        <circle cx="5" cy="3.5" r="1.25"/><circle cx="11" cy="3.5" r="1.25"/>
        <circle cx="5" cy="8" r="1.25"/><circle cx="11" cy="8" r="1.25"/>
        <circle cx="5" cy="12.5" r="1.25"/><circle cx="11" cy="12.5" r="1.25"/>
    </svg>
);

const LockIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14" aria-hidden="true">
        <rect x="3" y="11" width="18" height="11" rx="2"/>
        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
    </svg>
);

const ChevronIcon = ({ open }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"
        style={{ transition: 'transform 0.2s', transform: open ? 'rotate(90deg)' : 'rotate(0deg)' }}>
        <polyline points="9 18 15 12 9 6"/>
    </svg>
);

// Returns all page IDs already placed somewhere in the menu tree
const getUsedIds = (items) => {
    const ids = new Set();
    items.forEach(item => {
        ids.add(String(item.id));
        (item.children || []).forEach(child => ids.add(String(child.id)));
    });
    return ids;
};

// ── Child item row ───────────────────────────────────────────────────────────

function ChildSortableItem({ item, onRemove }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: item.id,
        disabled: item.system,
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
    };

    const Icon = iconMap[item.icon];

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`nav-edit-child${item.system ? ' system' : ''}${isDragging ? ' dragging' : ''}`}
        >
            <span
                className="nav-edit-handle"
                title={item.system ? 'System item — cannot be moved' : 'Drag to reorder'}
                {...(!item.system ? { ...attributes, ...listeners } : {})}
            >
                {item.system ? <LockIcon /> : <GripIcon />}
            </span>
            <span className="nav-edit-icon">{Icon && <Icon />}</span>
            <span className="nav-edit-label">{item.label}</span>
            {!item.system && (
                <button
                    type="button"
                    className="nav-edit-remove"
                    title="Remove"
                    onClick={() => onRemove(item.id)}
                >
                    ×
                </button>
            )}
        </div>
    );
}

// ── Top-level item row ───────────────────────────────────────────────────────

function SortableItem({
    item,
    isExpanded,
    onToggleExpand,
    onRemove,
    onChildDragEnd,
    onChildRemove,
    onChildAdd,
    availablePages, // globally filtered — pages not yet used anywhere in menu
}) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: item.id,
        disabled: item.system,
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
    };

    const Icon = iconMap[item.icon];

    // Nested DndContext is independent from the outer one — @dnd-kit supports
    // nesting; child IDs are not in the outer SortableContext so no conflict.
    const childSensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
    );

    const [addChildMode, setAddChildMode] = useState(false);
    const [selectedChildId, setSelectedChildId] = useState('');

    const children = [...(item.children || [])].sort((a, b) => a.position - b.position);

    const handleAddChild = () => {
        const found = availablePages.find(p => String(p.id) === selectedChildId);
        if (!found) return;
        onChildAdd(item.id, { type: 'link', icon: 'FileIcon', path: '/' + found.slug, label: found.name, ...found, system: false, position: children.length });
        setSelectedChildId('');
        setAddChildMode(false);
    };

    return (
        <div className="nav-edit-item-wrapper">
            <div
                ref={setNodeRef}
                style={style}
                className={`nav-edit-item${item.system ? ' system' : ''}${isDragging ? ' dragging' : ''}`}
            >
                <span
                    className="nav-edit-handle"
                    title={item.system ? 'System item — cannot be moved' : 'Drag to reorder'}
                    {...(!item.system ? { ...attributes, ...listeners } : {})}
                >
                    {item.system ? <LockIcon /> : <GripIcon />}
                </span>
                <span className="nav-edit-icon">{Icon && <Icon />}</span>
                <span className="nav-edit-label">{item.label}</span>

                <span className="nav-edit-item-controls">
                    {item.type === 'submenu' && (
                        <button
                            type="button"
                            className="nav-edit-expand"
                            title={isExpanded ? 'Collapse' : 'Edit items'}
                            onClick={() => onToggleExpand(item.id)}
                        >
                            <ChevronIcon open={isExpanded} />
                        </button>
                    )}
                    {!item.system && (
                        <button
                            type="button"
                            className="nav-edit-remove"
                            title="Remove"
                            onClick={() => onRemove(item.id)}
                        >
                            ×
                        </button>
                    )}
                </span>
            </div>

            {item.type === 'submenu' && isExpanded && (
                <div className="nav-edit-children">
                    {item.childrenSourse ? (
                        <p className="nav-edit-auto-source">
                            <LockIcon /> Auto-filled from <strong>{item.childrenSourse}</strong>
                        </p>
                    ) : (
                        <>
                            <DndContext
                                sensors={childSensors}
                                collisionDetection={closestCenter}
                                onDragEnd={({ active, over }) => {
                                    if (!over || active.id === over.id) return;
                                    const oldIndex = children.findIndex(c => c.id === active.id);
                                    const newIndex = children.findIndex(c => c.id === over.id);
                                    if (oldIndex === -1 || newIndex === -1) return;
                                    onChildDragEnd(item.id, oldIndex, newIndex);
                                }}
                            >
                                <SortableContext
                                    items={children.map(c => c.id)}
                                    strategy={verticalListSortingStrategy}
                                >
                                    {children.map(child => (
                                        <ChildSortableItem
                                            key={child.id}
                                            item={child}
                                            onRemove={(childId) => onChildRemove(item.id, childId)}
                                        />
                                    ))}
                                </SortableContext>
                            </DndContext>

                            {children.length === 0 && (
                                <p className="nav-edit-empty">No items yet</p>
                            )}

                            <div className="nav-edit-add">
                                {!addChildMode && availablePages.length > 0 && (
                                    <button type="button" onClick={() => setAddChildMode(true)}>
                                        + Add page
                                    </button>
                                )}
                                {addChildMode && (
                                    <div className="nav-edit-add-form">
                                        <select
                                            value={selectedChildId}
                                            onChange={e => setSelectedChildId(e.target.value)}
                                            autoFocus
                                        >
                                            <option value="">Select page…</option>
                                            {availablePages.map(p => (
                                                <option key={p.id} value={String(p.id)}>{p.name}</option>
                                            ))}
                                        </select>
                                        <button type="button" className="primary" onClick={handleAddChild} disabled={!selectedChildId}>
                                            Add
                                        </button>
                                        <button type="button" onClick={() => { setAddChildMode(false); setSelectedChildId(''); }}>
                                            Cancel
                                        </button>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

// ── Main edit component ──────────────────────────────────────────────────────

const NavMenuEdit = ({ initialItems, availablePages, onSave, onCancel }) => {
    const [items, setItems] = useState(() =>
        [...initialItems].sort((a, b) => a.position - b.position)
    );
    const [expandedGroups, setExpandedGroups] = useState(new Set());
    const [addMode, setAddMode] = useState(null); // null | 'link' | 'group'
    const [selectedId, setSelectedId] = useState('');
    const [groupName, setGroupName] = useState('');
    const [loading, setLoading] = useState(false);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
    );

    // Pages not yet placed anywhere in the current menu tree
    const usedIds = getUsedIds(items);
    const notYetPlaced = availablePages.filter(p => !usedIds.has(String(p.id)));

    const toggleExpand = (id) => {
        setExpandedGroups(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    // ── Top-level operations ─────────────────────────────────────────────────

    const handleDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return;
        const oldIndex = items.findIndex(i => i.id === active.id);
        const newIndex = items.findIndex(i => i.id === over.id);
        if (oldIndex === -1 || newIndex === -1) return;
        if (newIndex === 0 && !items[oldIndex].system) return; // cannot be first
        setItems(arrayMove(items, oldIndex, newIndex).map((item, idx) => ({ ...item, position: idx })));
    };

    const handleRemove = (id) => {
        setItems(items.filter(i => i.id !== id).map((item, idx) => ({ ...item, position: idx })));
    };

    const handleAddPage = () => {
        const found = notYetPlaced.find(p => String(p.id) === selectedId);
        if (!found) return;
        setItems([...items, { type: 'link', icon: 'FileIcon', path: '/' + found.slug, label: found.name, ...found, system: false, position: items.length }]);
        setSelectedId('');
        setAddMode(null);
    };

    const handleAddGroup = () => {
        if (!groupName.trim()) return;
        setItems([...items, {
            id: `custom-${Date.now()}`,
            type: 'submenu',
            label: groupName.trim(),
            icon: 'DefaultFolderIcon',
            system: false,
            position: items.length,
            permission: null,
            module: null,
            condition: null,
            children: [],
        }]);
        setGroupName('');
        setAddMode(null);
    };

    // ── Children operations ──────────────────────────────────────────────────

    const handleChildDragEnd = (parentId, oldIndex, newIndex) => {
        setItems(items.map(item => {
            if (item.id !== parentId) return item;
            const sorted = [...(item.children || [])].sort((a, b) => a.position - b.position);
            return {
                ...item,
                children: arrayMove(sorted, oldIndex, newIndex).map((c, idx) => ({ ...c, position: idx })),
            };
        }));
    };

    const handleChildRemove = (parentId, childId) => {
        setItems(items.map(item => {
            if (item.id !== parentId) return item;
            return {
                ...item,
                children: (item.children || [])
                    .filter(c => c.id !== childId)
                    .map((c, idx) => ({ ...c, position: idx })),
            };
        }));
    };

    const handleChildAdd = (parentId, newChild) => {
        setItems(items.map(item => {
            if (item.id !== parentId) return item;
            const existing = item.children || [];
            return {
                ...item,
                children: [...existing, { ...newChild, position: existing.length }],
            };
        }));
    };

    // ────────────────────────────────────────────────────────────────────────

    return (
        <div className="nav-edit">
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={items.map(i => i.id)} strategy={verticalListSortingStrategy}>
                    {items.map(item => (
                        <SortableItem
                            key={item.id}
                            item={item}
                            isExpanded={expandedGroups.has(item.id)}
                            onToggleExpand={toggleExpand}
                            onRemove={handleRemove}
                            onChildDragEnd={handleChildDragEnd}
                            onChildRemove={handleChildRemove}
                            onChildAdd={handleChildAdd}
                            availablePages={notYetPlaced}
                        />
                    ))}
                </SortableContext>
            </DndContext>

            <div className="nav-edit-add">
                {addMode === null && (
                    <div className="nav-edit-add-buttons">
                        {notYetPlaced.length > 0 && (
                            <button type="button" onClick={() => setAddMode('link')}>
                                + Add page
                            </button>
                        )}
                        <button type="button" onClick={() => setAddMode('group')}>
                            + Add group
                        </button>
                    </div>
                )}

                {addMode === 'link' && (
                    <div className="nav-edit-add-form">
                        <select value={selectedId} onChange={e => setSelectedId(e.target.value)}>
                            <option value="">Select page…</option>
                            {notYetPlaced.map(p => (
                                <option key={p.id} value={String(p.id)}>{p.name}</option>
                            ))}
                        </select>
                        <button type="button" onClick={handleAddPage} disabled={!selectedId}>Add</button>
                        <button type="button" onClick={() => { setAddMode(null); setSelectedId(''); }}>Cancel</button>
                    </div>
                )}

                {addMode === 'group' && (
                    <div className="nav-edit-add-form">
                        <input
                            type="text"
                            placeholder="Group name"
                            value={groupName}
                            onChange={e => setGroupName(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleAddGroup()}
                            autoFocus
                        />
                        <button type="button" className="primary" onClick={handleAddGroup} disabled={!groupName.trim()}>Add</button>
                        <button type="button" onClick={() => { setAddMode(null); setGroupName(''); }}>Cancel</button>
                    </div>
                )}
            </div>

            <div className="nav-edit-actions">
                <button type="button" className="primary" onClick={() => {
                    setLoading(true);
                    onSave(items);
                }}>{loading ? <><Loading className="btn-loader" />Loading...</> : 'Save'}</button>
                <button type="button" onClick={onCancel}>Cancel</button>
            </div>
        </div>
    );
};

export default NavMenuEdit;
